declare global {
  interface Window {
    dataLayer: IArguments[]
    gtag: {
      (command: 'js', date: Date): void
      (command: 'config', targetId: string, config?: Record<string, unknown>): void
      (command: 'set', key: string, value: Record<string, unknown>): void
      (command: 'event', eventName: string, params?: Record<string, unknown>): void
    }
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

// ─── Typed event map ──────────────────────────────────────────────────────────
// Add events here as you instrument new interactions.
// undefined = no properties required.

export type EventMap = {
  // Auth
  signup_completed: { method: 'email' | 'google'; referral_source?: string }
  login_completed: { method: 'email' | 'google' }
  logout: undefined
  // Onboarding
  onboarding_started: { user_type: string }
  onboarding_step_completed: { step: number; step_name: string }
  onboarding_completed: { user_type?: string; duration_seconds?: number }
  referral_source_submitted: { source: string }
  // Evaluation
  evaluation_requested: { startup_id: string }
  evaluation_completed: { startup_id: string; duration_seconds?: number }
  evaluation_viewed: { startup_id: string }
  // Data room
  dataroom_link_created: { startup_id: string }
  dataroom_link_copied: { startup_id: string; is_first: boolean }
  investor_analytics_opened: { startup_id: string }
  // See their take modal
  see_their_take_modal_opened: {
    startup_id: string
    investor_id: string
    is_prescreen_only: boolean
  }
  see_their_take_dismissed: { startup_id: string; investor_id: string }
  see_their_take_record_session_clicked: { startup_id: string; investor_id: string }
  see_their_take_confirmed: {
    startup_id: string
    investor_id: string
    variant: 'evaluate' | 'buy'
  }
  // Investor outreach
  investor_contact_clicked: {
    startup_id: string
    investor_id: string
    contact_id: string
    source: 'evaluation' | 'matches'
  }
  investor_message_copied: { startup_id: string; investor_id: string; contact_id: string }
  investor_linkedin_opened: { startup_id: string; investor_id: string; contact_id: string }
  investor_hook_regenerated: { startup_id: string; investor_id: string; contact_id: string }
  investor_body_regenerated: { startup_id: string; investor_id: string; contact_id: string }
  investor_profile_edit_clicked: { startup_id: string; investor_id: string; contact_id: string }
  // Automated outreach (founder-side campaign actions)
  outreach_linkedin_connect_started: { startup_id: string }
  outreach_invitation_link_clicked: { startup_id: string }
  outreach_linkedin_account_link_clicked: { startup_id: string }
  outreach_campaign_paused: { startup_id: string }
  outreach_campaign_resumed: { startup_id: string }
  outreach_messages_edit_opened: { startup_id: string }
  outreach_messages_saved: { startup_id: string }
  outreach_messages_reviewed: { startup_id: string }
  outreach_launch_checklist_opened: { startup_id: string }
  outreach_setup_step_clicked: { startup_id: string; step: string }
  outreach_campaign_launched: { startup_id: string }
  outreach_linkedin_disconnect_requested: { startup_id: string }
  // Portfolio founder outreach (second campaign)
  portfolio_outreach_enabled: { startup_id: string }
  portfolio_outreach_launched: { startup_id: string }
  portfolio_messages_saved: { startup_id: string }
  portfolio_messages_reviewed: { startup_id: string }
  outreach_topup_opened: { startup_id: string; reason: 'manual' | 'budget_paused' }
  outreach_topup_checkout_started: { startup_id: string; value: number; currency: string }
  outreach_withdrawal_requested: { startup_id: string }
  outreach_billing_portal_opened: { startup_id: string }
  outreach_lead_reviewed: { startup_id: string; investor_id: string }
  outreach_lead_approved: { startup_id: string; investor_id: string }
  outreach_lead_declined: { startup_id: string; investor_id: string }
  outreach_lead_archived: { startup_id: string; investor_id: string }
  // Public share link (/s/:token). Deliberately carries NO startup identifier:
  // this fires from an anonymous third party's browser, and naming the startup
  // would tell Google which private company is raising and who passed on it.
  // Per-startup detail lives in the first-party analytics the founder already
  // sees. Keep these free of anything that identifies the company or visitor.
  dataroom_interest_gate_answered: {
    decision: 'interested' | 'not_a_fit'
    reason?: string
    has_note: boolean
    is_second_thought: boolean
  }
  // Ask the profile (the chat on /s/:token). Same no-startup-identifier rule as
  // the block above, plus two of its own:
  //  - **no question text.** The surface promises the investor that the founder
  //    never sees what they ask; shipping the question to Google instead would
  //    be a worse version of the same breach.
  //  - **no slide number.** To anyone holding the deck, "slide 7" names the
  //    company as surely as its name does.
  // Ordinals and closed vocabularies only. The question itself, its topic and
  // its citations live in the first-party ledger, readable on the admin corpus
  // page and nowhere else.
  ask_profile_opened: { entry: 'chip' | 'typed' | 'slide' }
  ask_profile_question_sent: {
    entry: 'chip' | 'typed' | 'slide' | 'followup'
    preset?: 'team' | 'traction' | 'product' | 'moat' | 'deal_terms'
    question_index: number
  }
  ask_profile_answer_completed: { entry: string; duration_seconds: number; truncated: boolean }
  ask_profile_answer_failed: { entry: string; reason: 'network' | 'stream' | 'server' }
  ask_profile_followup_clicked: { question_index: number }
  ask_profile_slide_asked: undefined
  ask_profile_answer_copied: undefined
  ask_profile_ask_founder_clicked: undefined
  ask_profile_door_opened: {
    door: 'agent_follow' | 'thesis_fit'
    placement:
      | 'cta_bar'
      | 'deck_end'
      | 'question_limit'
      | 'evaluation_question'
      | 'after_connect'
      | 'pass_path'
      // The post-gate card at the foot of the profile, where "Follow this
      // company" replaced the diligence upsell that walled anonymous visitors.
      | 'post_gate'
  }
  ask_profile_door_submitted: { door: 'agent_follow' | 'thesis_fit'; had_email: boolean }
  // Follow from a public profile. Same no-startup-identifier rule as the share-link block above.
  share_follow_clicked: { signed_in_investor: boolean }
  share_follow_result: { result: 'following' | 'waiting' | 'error' }
  // Investor's assistant pages (signed in). Startup ids only; never a name, condition, quote or note.
  assistant_setup_copied: {
    target: 'server_address' | 'claude_code' | 'codex_add' | 'codex_login'
  }
  assistant_startup_link_clicked: { surface: 'follows' | 'alerts'; startup_id: string }
  assistant_follow_stopped: { startup_id: string }
  startup_viewed: {
    startup_id: string
    surface: 'panel' | 'page' | 'link'
    source?: 'following' | 'search'
  }
  follow_started: { startup_id: string; with_condition: boolean; surface: 'panel' | 'page' }
  follow_edited: { startup_id: string; with_condition: boolean }
  follow_cancelled: { startup_id: string }
  update_source_opened: { startup_id: string }
  see_plans_clicked: { startup_id: string }
  note_saved: { startup_id: string; cleared: boolean }
  assistant_note_deleted: { startup_id: string }
  assistant_pass_reopened: { startup_id: string }
  assistant_alerts_marked_read: { scope: 'all' | 'one' }
  assistant_alert_emails_toggled: { muted: boolean; surface: 'alerts' | 'privacy' | 'settings' }
  assistant_privacy_setting_changed: {
    setting: 'share_feedback' | 'keep_lookups'
    enabled: boolean
  }
  assistant_tab_selected: { assistant: string }
  assistant_open_clicked: { assistant: string }
  assistant_admin_request_copied: undefined
  assistant_setup_details_requested: { assistant: string }
  assistant_chatgpt_notify: undefined
  assistant_connected: { assistant: string }
  assistant_ask_clicked: { workflow: string; mode: 'open' | 'copy' }
  assistant_disconnected: { assistant: string }
  assistant_data_exported: undefined
  assistant_data_deleted: undefined
  // Connecting an assistant (the OAuth screens)
  oauth_consent_viewed: undefined
  oauth_consent_decided: { decision: 'connect' | 'cancel' }
  oauth_account_switch_clicked: { step: 'sign_in' | 'consent' }
  // Founder side: what investors' AI did, and email updates
  ai_reads_panel_viewed: { startup_id: string; line_count: number }
  email_update_address_copied: { startup_id: string }
  email_update_removed: { startup_id: string }
  follower_removed: { startup_id: string }
}

// ─── Core ─────────────────────────────────────────────────────────────────────

export function initGtag() {
  if (!GA_ID) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function () {
    // biome-ignore lint/complexity/noArguments: gtag API requires the arguments object
    window.dataLayer.push(arguments)
  } as unknown as Window['gtag']
  window.gtag('js', new Date())
  window.gtag('config', GA_ID, { send_page_view: false })
  window.gtag('set', 'user_properties', { surface: 'app' })
}

export function trackPageView(path: string) {
  if (!window.gtag) return
  window.gtag('event', 'page_view', { page_path: path })
}

/** Type-safe event tracking. Swap the body here to change the analytics backend. */
export function trackEvent<T extends keyof EventMap>(
  name: T,
  ...args: EventMap[T] extends undefined ? [] : [EventMap[T]]
) {
  if (!window.gtag) return
  window.gtag('event', name, args[0] as Record<string, unknown> | undefined)
}

/** Call after sign-in to attach user identity and properties to GA sessions. */
export function identifyUser(
  userId: string,
  props: { user_type: 'startup' | 'investor' | 'admin'; plan?: string }
) {
  if (!window.gtag || !GA_ID) return
  window.gtag('config', GA_ID, { user_id: userId })
  window.gtag('set', 'user_properties', props)
}
